import { useState } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@april9au/react-common/utils';

import type { ChatImage } from '@/chat/types';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { PrismicMedia } from '@/components/ui/media';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { hasText, PlainText, RichText } from './rich-text';

/**
 * Tapping a picture in the thread opens it here: the full image, its caption
 * and its long description, in a sheet that rises from the bottom of a phone
 * and sits as a centred panel from `sm` up — `DialogContent`'s geometry, for
 * the reason given there. A dialog anchored to the bottom of a 1440 px window
 * is a phone convention pointed at a desktop.
 *
 * A sheet rather than a lightbox because the description is the point — the
 * learner is reading, not just looking. Which also means a carousel has to be
 * walkable from in here: the sheet is where the words are, so closing it to
 * reach the next picture's words is the one thing it must not require.
 */

export type ImageSheetProps = {
  images: Array<ChatImage>;
  /** Index of the image to show, or `null` when the sheet is closed. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
};

export function ImageSheet({ images, index, onIndexChange }: ImageSheetProps) {
  const { chat } = useStrings();
  const image = index === null ? undefined : images[index];
  const many = images.length > 1;
  const at = index ?? 0;

  return (
    <DialogPrimitive.Root open={image !== undefined} onOpenChange={(open) => !open && onIndexChange(null)}>
      <DialogPrimitive.Portal>
        {/* `black/50` and the small static blur are `DialogOverlay`'s and
            `SheetOverlay`'s, so every scrim in the app is the same ground. */}
        <DialogPrimitive.Overlay className="data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)]" />
        <DialogPrimitive.Content
          data-slot="chat-image-sheet"
          className={cn(
            // `text-chat-ink` on the sheet itself, not just on its headings: the
            // portal renders under `<body>`, so the close button's `ghost`
            // variant inherited the document's white and drew an invisible
            // cross on a white sheet.
            'bg-bubble-buddy text-chat-ink fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl',
            'shadow-[0_-8px_40px_-12px_hsl(var(--surf-dark-blue)/0.45)]',
            'data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            'duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)] data-[state=closed]:duration-[var(--bp-duration-fast)] data-[state=closed]:ease-[var(--bp-ease-in)]',
            // The home indicator's inset below `sm`, where the sheet is a fixed
            // bottom edge. The centred panel is nowhere near it.
            'pb-[env(safe-area-inset-bottom,0px)]',
            // A centred card from `sm` up, on `DialogContent`'s terms: the same
            // radius, the same downward elevation, the same zoom rather than a
            // slide, and a height that leaves the window's own margin visible.
            'sm:inset-x-auto sm:top-[50%] sm:bottom-auto sm:left-[50%] sm:max-h-[calc(100dvh-3rem)] sm:-translate-x-[50%] sm:-translate-y-[50%] sm:rounded-3xl sm:pb-0',
            'sm:shadow-[0_32px_80px_-34px_hsl(var(--surf-dark-blue)/0.85)]',
            'sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95',
            // Fades in place under reduced motion — the shared rule in
            // `app.css`, which needs the `data-slot` above to reach this.
            'bp-overlay',
          )}
        >
          {image === undefined ? null : (
            <>
              <div className="flex items-start justify-between gap-3 px-5 pt-5">
                <DialogPrimitive.Title className="text-chat-ink font-bebas min-w-0 text-2xl leading-none tracking-wide uppercase">
                  <PlainText field={image.caption} />
                </DialogPrimitive.Title>
                {/* The header's icon button: blue on a blue wash, 44 px. */}
                <DialogPrimitive.Close asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={chat.chat_image_close}
                    className="text-surf-blue hover:bg-surf-blue/[0.08] -mt-2 size-11 shrink-0 rounded-full"
                  >
                    <Icon name="passport/close" aria-hidden className="size-5" />
                  </Button>
                </DialogPrimitive.Close>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-4 pb-6">
                {image.image.url === null ? null : (
                  <PrismicMedia
                    image={image.image}
                    priority
                    placeholder="var(--chat-rail)"
                    className="w-full rounded-xl outline outline-black/10"
                  />
                )}
                {hasText(image.description) ? (
                  <DialogPrimitive.Description asChild>
                    <div className="text-chat-ink mt-4 text-[15px] leading-relaxed">
                      <RichText field={image.description} />
                    </div>
                  </DialogPrimitive.Description>
                ) : null}
              </div>

              {/*
                The walk through a carousel, in the thumb zone rather than in
                the header where the count used to sit on its own: the count is
                only useful next to the thing that changes it. Pinned outside
                the scroller, so a long description never scrolls the way
                forward off the sheet.

                Both ends wrap rather than disable. A button that disables
                itself under the finger that just pressed it drops the keyboard
                focus on the floor, and "1 of 3" already says where the walk
                has got to.
              */}
              {many ? (
                <div className="border-chat-hairline flex shrink-0 items-center justify-between gap-3 border-t px-5 py-3">
                  <Button
                    variant="outline"
                    aria-label={chat.chat_image_previous}
                    onClick={() => onIndexChange((at - 1 + images.length) % images.length)}
                    className="size-11 shrink-0 rounded-full p-0"
                  >
                    <Icon name="passport/chevron-left" aria-hidden className="size-5 rtl:rotate-180" />
                  </Button>
                  <p className="text-chat-ink-muted text-xs font-semibold tabular-nums">
                    {formatString(chat.chat_image_of, { current: at + 1, total: images.length })}
                  </p>
                  <Button
                    variant="outline"
                    aria-label={chat.chat_image_next}
                    onClick={() => onIndexChange((at + 1) % images.length)}
                    className="size-11 shrink-0 rounded-full p-0"
                  >
                    <Icon name="passport/chevron-right" aria-hidden className="size-5 rtl:rotate-180" />
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Owns the "which image is open" state for a message that has pictures. */
export function useImageSheet() {
  const [index, setIndex] = useState<number | null>(null);

  return { index, open: setIndex, close: () => setIndex(null), onIndexChange: setIndex };
}
